import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  useGithubLinkInitiate,
  useGithubLinkStatus,
  useGithubLinkUnlink,
  useGithubStatus,
} from '@/hooks/api/useGithub';
import { useToast } from '@/hooks/use-toast';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { getApiErrorMessage } from '@/lib/api-errors';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { ExternalLink, Github, Link, Loader2, Unlink } from 'lucide-react';
import { useState, type ReactElement } from 'react';

interface GitHubLinkButtonProps {
  organizationId: string;
  /** Optional: Show as a compact badge instead of full card */
  variant?: 'full' | 'compact';
  /** Optional: Custom class name */
  className?: string;
}

/**
 * GitHubLinkButton Component
 *
 * Allows users to link their GitHub account to their Kirin account.
 * Shows different states based on:
 * - Feature flag enabled/disabled
 * - Organization connected to GitHub
 * - User's account linked/not linked
 *
 * When clicked, initiates the OAuth flow to link the account.
 */
export function GitHubLinkButton({
  organizationId,
  variant = 'full',
  className,
}: GitHubLinkButtonProps): ReactElement | null {
  const { toast } = useToast();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Feature flag check
  const { flags, isLoading: flagsLoading } = useFeatureFlags([
    flagsmithFeatureFlags.GITHUB_INTEGRATION,
  ]);

  // Check organization's GitHub connection status
  const { data: orgStatus, isLoading: orgStatusLoading } =
    useGithubStatus(organizationId);

  // Check user's link status
  const { data: linkStatus, isLoading: linkStatusLoading } =
    useGithubLinkStatus(organizationId, {
      // Only check link status if org is connected
      enabled: orgStatus?.connected === true,
    });

  // Link initiate mutation
  const linkInitiateMutation = useGithubLinkInitiate(organizationId);

  // Unlink mutation
  const unlinkMutation = useGithubLinkUnlink(organizationId);

  // Feature flag check
  const isFeatureEnabled =
    import.meta.env.VITE_FF_GITHUB_INTEGRATION === 'true' ||
    flags[flagsmithFeatureFlags.GITHUB_INTEGRATION] ||
    false;

  // Don't show if feature is disabled
  if (!flagsLoading && !isFeatureEnabled) {
    return null;
  }

  // Don't show if org is not connected to GitHub
  if (!orgStatusLoading && !orgStatus?.connected) {
    return null;
  }

  const isLoading =
    flagsLoading ||
    orgStatusLoading ||
    linkStatusLoading ||
    linkInitiateMutation.isPending ||
    unlinkMutation.isPending ||
    isRedirecting;
  const isLinked = linkStatus?.isLinked ?? false;
  const linkedLogin = linkStatus?.providerLogin;

  const handleLinkClick = async (): Promise<void> => {
    try {
      const result = await linkInitiateMutation.mutateAsync();

      // Check if feature is disabled
      if ('enabled' in result && result.enabled === false) {
        toast({
          title: 'Feature not available',
          description: 'GitHub integration is not currently enabled.',
          variant: 'destructive',
        });
        return;
      }

      // Redirect to GitHub OAuth
      setIsRedirecting(true);
      window.location.href = result.redirectUrl;
    } catch {
      toast({
        title: 'Link failed',
        description:
          'Failed to start GitHub account linking. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleUnlinkClick = async (): Promise<void> => {
    try {
      const result = await unlinkMutation.mutateAsync();

      // Check if feature is disabled
      if ('enabled' in result && result.enabled === false) {
        toast({
          title: 'Feature not available',
          description: 'GitHub integration is not currently enabled.',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'GitHub account unlinked',
        description:
          'Your GitHub account has been unlinked. You can now link a different account.',
      });
    } catch (err) {
      toast({
        title: 'Unlink failed',
        description: getApiErrorMessage(
          err,
          'Failed to unlink your GitHub account. Please try again.'
        ),
        variant: 'destructive',
      });
    }
  };

  // Shared unlink confirmation dialog content
  const renderUnlinkDialogContent = (): ReactElement => (
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Unlink GitHub Account</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you want to unlink your GitHub account (@{linkedLogin})?
          You can link a different account afterwards.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={handleUnlinkClick}>
          Unlink
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  );

  // Compact variant - just shows a badge/button
  if (variant === 'compact') {
    if (isLinked) {
      return (
        <div className={`flex items-center gap-2 ${className ?? ''}`}>
          <Badge variant="outline">
            <Github className="h-3 w-3 mr-1" />@{linkedLogin}
          </Badge>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled={isLoading}
                className="h-6 px-2"
              >
                {unlinkMutation.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Unlink className="h-3 w-3" />
                )}
              </Button>
            </AlertDialogTrigger>
            {renderUnlinkDialogContent()}
          </AlertDialog>
        </div>
      );
    }

    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleLinkClick}
        disabled={isLoading}
        className={className}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <Github className="h-4 w-4 mr-1" />
            Link GitHub
          </>
        )}
      </Button>
    );
  }

  // Full variant - card with more details
  return (
    <div className={`border rounded-lg p-4 ${className ?? ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-muted rounded-md">
            <Github className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-medium">GitHub Account</h3>
            {isLinked ? (
              <p className="text-sm text-muted-foreground">
                Linked to{' '}
                <span className="font-mono text-foreground">
                  @{linkedLogin}
                </span>
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Link your GitHub account to enable identity tracking
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isLinked ? (
            <>
              <Badge variant="secondary" className="gap-1">
                <Link className="h-3 w-3" />
                Linked
              </Badge>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isLoading}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {unlinkMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Unlink className="h-4 w-4 mr-2" />
                    )}
                    Unlink
                  </Button>
                </AlertDialogTrigger>
                {renderUnlinkDialogContent()}
              </AlertDialog>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={handleLinkClick}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <ExternalLink className="h-4 w-4 mr-2" />
              )}
              Link Account
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
